import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Button — WhiteLine design system
 *
 * Figma: Design-System-White-Line / Button
 * Sizes:
 *   - md (40px, radius 12, gap 4px, px 12, text 14)
 *   - lg (48px, radius 16, gap 8px, px 16, text 14)
 * Variants:
 *   - primary    — filled accent (#6A80FF), on-accent text, pressed #92A5FF
 *   - secondary  — bordered, text-primary
 *   - tertiary   — link-coloured text on transparent
 *
 * Legacy aliases (default/outline/ghost/destructive/link, size default/sm/icon)
 * are kept so existing call-sites don't break.
 */
const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center whitespace-nowrap font-sans font-semibold",
    "ring-offset-background transition-[background-color,border-color,color,box-shadow]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-accent text-on-accent",
          "hover:bg-accent-hover active:bg-accent-pressed",
          "disabled:bg-state-disabled-bg/[0.05] disabled:text-state-disabled-text/25",
        ].join(" "),
        secondary: [
          "border border-border-strong bg-transparent text-foreground",
          "hover:bg-foreground/[0.04]",
          "active:border-bg-subtle active:bg-transparent",
          "disabled:border-border disabled:text-state-disabled-text/25",
        ].join(" "),
        tertiary: [
          "bg-transparent text-link",
          "hover:bg-accent-soft/60",
          "active:bg-accent-soft",
          "disabled:bg-transparent disabled:font-normal disabled:text-state-disabled-text/25",
        ].join(" "),

        /* ---------- Legacy shadcn aliases (kept for compatibility) ---------- */
        default:
          "bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-pressed disabled:bg-state-disabled-bg/[0.05] disabled:text-state-disabled-text/25",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80",
        outline:
          "border border-border-strong bg-transparent text-foreground hover:bg-foreground/[0.04] active:border-bg-subtle",
        ghost: "bg-transparent text-foreground hover:bg-foreground/[0.06]",
        link: "text-link underline-offset-4 hover:underline",
      },
      size: {
        md: "h-10 gap-1 rounded-[12px] px-3 text-sm [&_svg]:size-5",
        lg: "h-12 gap-2 rounded-2xl px-4 text-sm [&_svg]:size-5",

        /* legacy size aliases */
        default: "h-10 gap-1 rounded-[12px] px-3 text-sm [&_svg]:size-5",
        sm: "h-9 gap-1 rounded-lg px-3 text-sm [&_svg]:size-4",
        icon: "h-10 w-10 rounded-[12px] [&_svg]:size-5",
        "icon-lg": "h-12 w-12 rounded-2xl [&_svg]:size-5",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    if (asChild) {
      return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" aria-hidden /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };